import React from "react";
import {
  Card,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Remove as RemoveIcon,
  LocalCafe as LocalCafeIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDate } from "@/utility/useDate";
import StatusBadge from "./StatusBadge";
import LiveDuration from "./LiveDuration";


export interface IDriverMember {
  uuid: string;
  name: string;
  assignment: string;
  status: "on-shift" | "on-break" | "off-shift";
  statusStartTime?: string;
  breakStartTime?: string;
  shiftCarParkName?: string;
}

interface DriverCardProps {
  driver: IDriverMember;
  onStatusChange: (uuid: string, newStatus: "on-shift" | "on-break" | "off-shift") => void;
  onTaskChange?: (uuid: string, newTask: string) => void;
  onViewTimeline?: (uuid: string) => void;
  animationIndex?: number;
  canEdit: boolean;
  tasks?: Array<{ id: number; key: string; title: string }>;
  isPending?: boolean;
}

function DriverCard({ driver, onStatusChange, onTaskChange, onViewTimeline, animationIndex = 0, canEdit, tasks, isPending = false }: DriverCardProps) {
  const { t } = useTranslation();
  const { localizeDate } = useDate();

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "on-shift":
        return t("driverShiftTracker.status.onShift");
      case "on-break":
        return t("driverShiftTracker.status.onBreak");
      case "off-shift":
        return t("driverShiftTracker.status.offShift");
      default:
        return "";
    }
  };

  // fomat time for display (HH:mm)
  const formatTime = (timeString?: string) => {
    if (!timeString) return "";
    try {
      return localizeDate(timeString).toFormat("HH:mm");
    } catch {
      return "";
    }
  };

  const formatFullDateTime = (timeString?: string) => {
    if (!timeString) return "";
    try {
      return localizeDate(timeString).toFormat("MMM d, yyyy, h:mm a");
    } catch {
      return "";
    }
  };

  return (
    <Card
      sx={{
        padding: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: "transform 150ms ease, box-shadow 150ms ease",
        ...(animationIndex !== undefined && {
          animation: "fadeInUp 400ms cubic-bezier(0.4, 0, 0.2, 1) backwards",
          animationDelay: `${animationIndex * 50}ms`,
          "@keyframes fadeInUp": {
            from: {
              opacity: 0,
              transform: "translateY(20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }),
        "&:hover, &:focus-within": {
          transform: "translateY(-2px)",
          boxShadow: 3,
        },
      }}
      data-testid={`driver-card-${driver.uuid}`}
    >
      {/* name and status badge */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
        <Typography
          variant="h6"
          component="div"
          title={driver.name}
          sx={{
            fontWeight: "bold",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flex: 1,
            marginRight: 1,
          }}
          data-testid={`driver-name-${driver.uuid}`}
        >
          {driver.name}
        </Typography>

        <StatusBadge
          status={driver.status}
          label={getStatusLabel(driver.status)}
          data-testid={`status-badge-${driver.uuid}`}
        />
      </Box>

      {/* current assignment dropdown */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          marginBottom: 2,
          visibility: driver.status === "off-shift" ? "hidden" : "visible"
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content", whiteSpace: "nowrap" }}>
          {t("driverShiftTracker.labels.currentTask")}
        </Typography>
        <Select
          value={driver.assignment}
          size="small"
          variant="outlined"
          sx={{ flexGrow: 1 }}
          onChange={(e) => onTaskChange?.(driver.uuid, e.target.value)}
          disabled={!canEdit || isPending}
          data-testid={`task-select-${driver.uuid}`}
          endAdornment={
            isPending ? (
              <InputAdornment position="end" sx={{ marginRight: 3 }}>
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null
          }
        >
          {tasks?.map((task) => (
            <MenuItem key={task.id} value={task.key}>
              {task.title}
            </MenuItem>
          ))}
        </Select>
        {/* TODO: DD - Temporarily hidden
        <IconButton
          size="small"
          aria-label={t("driverShiftTracker.labels.viewTimeline")}
          onClick={() => onViewTimeline?.(driver.uuid)}
          data-testid={`view-timeline-button-${driver.uuid}`}
        >
          <HistoryIcon />
        </IconButton>
        */}
      </Box>

      {driver.status === "on-shift" && (
        <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
          {/* end shift button */}
          <Button
            variant="contained"
            size="small"
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
            onClick={() => onStatusChange(driver.uuid, "off-shift")}
            disabled={!canEdit || isPending}
            sx={{
              flex: 1,
              backgroundColor: (theme) => theme.palette.brand.main,
              color: (theme) => theme.palette.brand.contrastText,
              fontWeight: "bold",
              transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1)",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.brand.dark,
              },
              "&:active": {
                transform: "scale(0.97)",
              },
              "&:focus-visible": {
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
              },
            }}
            disableRipple
            disableElevation
            data-testid={`end-shift-button-${driver.uuid}`}
          >
            {t("driverShiftTracker.buttons.endShift")}
          </Button>

          {/* shiftbreak button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={isPending ? <CircularProgress size={16} /> : <LocalCafeIcon />}
            onClick={() => onStatusChange(driver.uuid, "on-break")}
            disabled={!canEdit || isPending}
            sx={{
              flex: 1,
              borderColor: (theme) => theme.palette.divider,
              color: "text.primary",
              fontWeight: "bold",
              transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1)",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.grey[100],
                borderColor: (theme) => theme.palette.divider,
              },
              "&:active": {
                transform: "scale(0.97)",
              },
              "&:focus-visible": {
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
              },
            }}
            disableRipple
            data-testid={`take-break-button-${driver.uuid}`}
          >
            {t("driverShiftTracker.buttons.startBreak")}
          </Button>
        </Box>
      )}

      {driver.status === "on-break" && (
        <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
          {/* end shift button */}
          <Button
            variant="contained"
            size="small"
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
            onClick={() => onStatusChange(driver.uuid, "off-shift")}
            disabled={!canEdit || isPending}
            sx={{
              flex: 1,
              backgroundColor: (theme) => theme.palette.brand.main,
              color: (theme) => theme.palette.brand.contrastText,
              fontWeight: "bold",
              transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1)",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.brand.dark,
              },
              "&:active": {
                transform: "scale(0.97)",
              },
              "&:focus-visible": {
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
              },
            }}
            disableRipple
            disableElevation
            data-testid={`end-shift-button-${driver.uuid}`}
          >
            {t("driverShiftTracker.buttons.endShift")}
          </Button>

          {/* resume shift button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={isPending ? <CircularProgress size={16} /> : undefined}
            onClick={() => onStatusChange(driver.uuid, "on-shift")}
            disabled={!canEdit || isPending}
            sx={{
              flex: 1,
              borderColor: (theme) => theme.palette.divider,
              color: "text.primary",
              fontWeight: "bold",
              transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1)",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.grey[100],
                borderColor: (theme) => theme.palette.divider,
              },
              "&:active": {
                transform: "scale(0.97)",
              },
              "&:focus-visible": {
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
              },
            }}
            disableRipple
            data-testid={`resume-shift-button-${driver.uuid}`}
          >
            {t("driverShiftTracker.buttons.endBreak")}
          </Button>
        </Box>
      )}

      {driver.status === "off-shift" && (
        <>
          <Box sx={{ marginBottom: 2 }}>
            {/* start shift button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={isPending ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              onClick={() => onStatusChange(driver.uuid, "on-shift")}
              disabled={!canEdit || isPending}
              sx={{
                width: "100%",
                borderColor: (theme) => theme.palette.divider,
                color: "text.primary",
                fontWeight: "bold",
                transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1)",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[100],
                  borderColor: (theme) => theme.palette.divider,
                },
                "&:active": {
                  transform: "scale(0.97)",
                },
                "&:focus-visible": {
                  boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                },
              }}
              disableRipple
              data-testid={`start-shift-button-${driver.uuid}`}
            >
              {t("driverShiftTracker.buttons.startShift")}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" data-testid={`off-shift-message-${driver.uuid}`}>
            {driver.shiftCarParkName
              ? t("driverShiftTracker.labels.onShiftAtOtherCarPark", { name: driver.shiftCarParkName })
              : t("driverShiftTracker.labels.notOnShift")}
          </Typography>
        </>
      )}

      {/* time section */}
      {driver.status !== "off-shift" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            minHeight: "20px",
            overflow: "hidden",
          }}
        >
          {driver.statusStartTime ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid={`shift-duration-${driver.uuid}`}
                title={formatFullDateTime(driver.statusStartTime)}
                sx={{
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                {t("driverShiftTracker.labels.started", { time: formatTime(driver.statusStartTime) })} · <LiveDuration startTime={driver.statusStartTime} precision="minutes" />
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid={`break-status-${driver.uuid}`}
                title={driver.breakStartTime ? formatFullDateTime(driver.breakStartTime) : undefined}
                sx={{
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: "1 1 auto",
                  minWidth: 0,
                  textAlign: "right",
                }}
              >
                {driver.status === "on-break" && driver.breakStartTime
                  ? <>{t("driverShiftTracker.labels.started", { time: formatTime(driver.breakStartTime) })} · <LiveDuration startTime={driver.breakStartTime} precision="seconds" /></>
                  : driver.breakStartTime
                    ? t("driverShiftTracker.labels.breakTaken")
                    : t("driverShiftTracker.labels.noBreakTaken")}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              &nbsp;
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};

export default React.memo(DriverCard);
